import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { Card, CardContent, CardHeader } from "@fronterahealth/frontera-ui-components/ui/card";

import { ReportReviewerStatus, ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { isSuggestedEdit } from "@components/QaReview/PeerReview/peerReview.suggestedEdit";
import { calculateRawPositions, orderItemsForNavigator } from "@components/QaReview/ReviewStack/positioning";
import { CheckAction } from "@components/QaReview/components/CommentActionResolve";
import { PersonAvatar } from "@components/QaReview/components/PersonAvatar";
import { getReviewUserDisplay } from "@components/QaReview/components/commentAuthor";
import { useGoToItem } from "@components/QaReview/hooks/useGoToItem";
import { useResolveItem } from "@components/QaReview/hooks/useResolveItem";
import { commentFieldTitle, domAnchorOf, fieldRowLabel } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useReviewFields } from "@components/QaReview/hooks/useReviewFields";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { Reviewer, formatReviewTime, latestSendBack, latestStatusEntry } from "@components/ReportReview/reviewers";

interface ToFixCardProps {
  reviewer: Reviewer;
  containerId: string;
}

/** The reviewer's comments, top to bottom as their fields sit on the page (feed order until the fields mount). */
const useReportOrder = (items: ReviewItemFieldsFragment[], containerId: string) => {
  // The report's fields mount after its data loads, so they are measured again then.
  const fieldsById = useReviewFields();
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  useEffect(() => {
    // After a frame, so the report's fields are on the page to measure.
    const frame = requestAnimationFrame(() => {
      const entries = items.map((item) => ({ id: item.id, anchorFieldId: domAnchorOf(item) }));
      const { positions, validItemIds } = calculateRawPositions(entries, containerId);
      setOrderedIds(orderItemsForNavigator(entries, positions, validItemIds, containerId).map((entry) => entry.id));
    });
    return () => cancelAnimationFrame(frame);
  }, [items, containerId, fieldsById]);

  return useMemo(() => {
    const rank = (item: ReviewItemFieldsFragment) => {
      const index = orderedIds.indexOf(item.id);
      return index < 0 ? Number.MAX_SAFE_INTEGER : index;
    };
    return [...items].sort((a, b) => rank(a) - rank(b));
  }, [items, orderedIds]);
};

const ToFixRow: React.FC<{ item: ReviewItemFieldsFragment }> = ({ item }) => {
  const goToItem = useGoToItem();
  // Acting from this list keeps the author's place instead of jumping to the next open card.
  const { resolve, isResolving } = useResolveItem({ advance: false });
  const done = isReviewItemResolved(item);
  // Accept writes the field and Keep mine resolves the card; neither may start while the other runs.
  const isApplying = useReviewSession().state.applyingFieldId !== null;
  const isKeeping = isResolving(item);

  return (
    <li className="flex items-center gap-2 rounded-md border border-limestone-200 bg-white px-2.5 py-1.5">
      <button
        type="button"
        data-dd-action-name="to-fix row go to field"
        // A done row only scrolls to its field; switching views would hide the author's remaining open cards.
        onClick={() => goToItem(item, { keepView: done })}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {done ? (
          <CheckCircleIcon className="size-4 shrink-0 text-emerald-500" />
        ) : (
          <span className="size-3.5 shrink-0 rounded-xs border border-limestone-400" />
        )}
        <Small className={cn("min-w-0 truncate", done && "text-text-tertiary line-through")}>
          <span className="font-medium">{fieldRowLabel(commentFieldTitle(item))}</span>
          {isSuggestedEdit(item) && <span className="text-text-tertiary"> · suggested edit</span>}
        </Small>
      </button>
      {!done && (
        <CheckAction comment={item} stayPut>
          {(action) =>
            action && (
              <span className="flex shrink-0 items-center gap-2">
                <Button
                  variant="link"
                  size="xs"
                  className="h-auto p-0 text-xs!"
                  data-dd-action-name={action.kind === "accept" ? "to-fix accept" : "to-fix mark fixed"}
                  disabled={action.disabled || isKeeping}
                  title={action.title}
                  onClick={action.run}
                >
                  {action.kind === "accept" ? "Accept" : "Mark fixed"}
                </Button>
                {action.kind === "accept" && (
                  <Button
                    variant="link"
                    size="xs"
                    className="h-auto p-0 text-xs!"
                    data-dd-action-name="to-fix keep mine"
                    disabled={isKeeping || isApplying}
                    onClick={() => resolve(item)}
                  >
                    Keep mine
                  </Button>
                )}
              </span>
            )
          }
        </CheckAction>
      )}
    </li>
  );
};

/**
 * What an author has left to fix after a reviewer sent the report back. `children` is the slot under the
 * header for the reviewer's note and the Ask to look again action.
 */
export const ToFixCard: React.FC<PropsWithChildren<ToFixCardProps>> = ({ reviewer, containerId, children }) => {
  const { rootComments } = useReviewData();
  const reviewerUser = reviewer.reviewer;
  const reviewerId = reviewerUser?.id;

  // This round: the reviewer's still-open comments from any round, plus whatever they wrote since the latest
  // time they were asked to review, so earlier rounds' resolved comments don't pad "N of M done".
  const roundStart = latestStatusEntry(reviewer, ReportReviewerStatus.Requested)?.createdDate;
  const items = useMemo(() => {
    const roundStartTime = roundStart ? Date.parse(roundStart) : null;
    return rootComments.filter(
      (item) =>
        item.__typename === "ReportCommentType" &&
        !item.isDeleted &&
        !item.hidden &&
        item.author?.id === reviewerId &&
        (!isReviewItemResolved(item) ||
          roundStartTime == null ||
          Date.parse(item.createdDate) >= roundStartTime ||
          // Fixed from this card this round: it stays, counted as done.
          (item.resolvedAt != null && Date.parse(item.resolvedAt) >= roundStartTime)),
    );
  }, [rootComments, reviewerId, roundStart]);
  const rows = useReportOrder(items, containerId);

  if (!reviewerUser) return null;
  const { name, initials } = getReviewUserDisplay(reviewerUser);
  const sentBackAt = latestSendBack(reviewer)?.createdDate;
  const doneCount = rows.filter(isReviewItemResolved).length;

  return (
    <Card size="sm" className="border-amber-200 bg-amber-50" data-testid="to-fix-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <PersonAvatar id={reviewerUser.id} initials={initials} size="sm" />
          <Small className="min-w-0 flex-1 truncate font-semibold">{name} sent this back</Small>
          {sentBackAt && (
            <Small className="shrink-0 whitespace-nowrap text-text-tertiary">{formatReviewTime(sentBackAt)}</Small>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {children}
        {rows.length > 0 && (
          <>
            <Small className="block text-text-tertiary">
              {doneCount} of {rows.length} done
            </Small>
            {/* Capped so a long list can't push every comment card far from its field. */}
            <ul className="max-h-64 space-y-1.5 overflow-y-auto">
              {rows.map((item) => (
                <ToFixRow key={item.id} item={item} />
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
};
